<template>
  <header class="bg-white dark:bg-neutral-900">
    <PageBanner :src="banner" :loading="loading" alt="organization banner" />
    <div class="px-4 lg:px-8 relative pb-4 z-[5]">
      <div class="header-grid">
        <AsyncImage
          class="header-grid-avatar"
          :src="avatar"
          :loading="loading"
          alt="org avatar"
        />
        <div class="header-grid-options flex items-start justify-end space-x-2">
          <NamiButton
            button-type="primary"
            text
            no-waves
            @button-click="goToSendMessage"
          >
            <IconMail :size="22.8" />
          </NamiButton>
          <NamiButton
            :button-type="isFollowing ? 'secondary' : 'primary'"
            :class="isFollowing ? 'w-[104px]' : 'w-24'"
            :disabled="initialFollowPending"
            @buttonClick="toggleFollow"
          >
            {{ isFollowing ? "Unfollow" : "Follow" }}
          </NamiButton>
          <ContextMenuList
            closeOnOutsideClick
            defaultPosition="bottom-left"
            class="inline-block relative"
            :options="[
              {
                name: 'Report',
                action: () => {
                  if (!user) appStore?.openLoginRequiredModal();
                  else showReportModal = true;
                },
              },
            ]"
          >
            <template v-slot="{ isOpen, open, close }">
              <NamiButton
                @buttonClick="() => (isOpen ? close() : open())"
                buttonType="secondary"
                :icon="IconDots"
                :text="!isOpen"
                iconPosition="right"
              />
            </template>
          </ContextMenuList>
        </div>
        <div class="header-grid-username flex items-center truncate space-x-2">
          <BadgeUsername v-if="displayName" :verified>
            {{ displayName }}
          </BadgeUsername>
        </div>
        <div v-if="org" class="header-grid-info text-sm sm:text-base">
          <TheNuxtLink
            :to="href.org.slug.followers(org.attributes.slug)"
            class="org_profile__followers hover:underline"
          >
            <i18n-t
              keypath="components.OrganizationProfileHeader.label.followers"
              :plural="followerCount"
            >
              <template #count>
                <span class="font-medium">{{
                  formatNumber(followerCount ?? 0)
                }}</span>
              </template>
            </i18n-t>
          </TheNuxtLink>
          <div v-if="org.attributes.tagline" class="mt-2">
            <IconSpeakerphone class="mr-2 inline" size="1.5em" />
            <span>{{ org.attributes.tagline }}</span>
          </div>
        </div>
        <div
          v-if="extractFromLocale(locale, org?.attributes.description ?? {})"
          class="header-grid-description text-sm sm:text-base"
        >
          {{ extractFromLocale(locale, org?.attributes.description ?? {}) }}
        </div>
      </div>
    </div>

    <div class="sm:px-4 lg:px-8 relative">
      <div class="container flex">
        <slot name="tabs"></slot>
      </div>
    </div>
  </header>
  <ReportModal
    v-if="orgId"
    v-model="showReportModal"
    entityType="organization"
    :entityId="orgId"
  />
</template>

<script setup lang="ts">
import { IconDots, IconSpeakerphone, IconMail } from "@tabler/icons-vue";
import AsyncImage from "~/components/async/AsyncImage.vue";
import { parseOrgForDisplay } from "~/utils/display/org";
import {
  type AvailableLanguages,
  Organization,
  type OrganizationEntity,
} from "~/src/api";
import type { Ref } from "vue";
import { injectTwilioContext } from "../app/context/sources/twilio/context";
import { useQuery } from "@tanstack/vue-query";

interface Props {
  org?: OrganizationEntity | null;
  loading?: boolean;
}

const props = defineProps<Props>();

const targetUsersForConversationCreation =
  useTargetParticipantsForNewConversation();
const { primaryTwilioClient } = injectTwilioContext();
const { conversations } = primaryTwilioClient;
const href = useHref();
const nuxtApp = useNuxtApp();
const authStore = nuxtApp.$auth();
const appStore = nuxtApp.$app();
const locale: Ref<AvailableLanguages> = nuxtApp.$i18n.global.locale;

const user = computed(() => authStore?.user);
const orgId = computed(() => props.org?.id);
const showReportModal = ref(false);

const formatter = new Intl.NumberFormat("en");
const formatNumber = (v: number) => formatter.format(v);

const { data: followerCount, suspense } = useQuery({
  queryKey: ["organization_follow", "collection", { organizationId: orgId }],
  queryFn: async () =>
    Organization.getFollows({ organizationId: orgId.value, limit: 0 }),
  select: (data) => data.meta.total,
});

onServerPrefetch(async () => {
  await suspense();
});

const { banner, avatar, displayName, verified } = parseOrgForDisplay(
  computed(() => props.org),
);

const { isFollowing, toggleFollow, initialFollowPending } = useFollow(
  "organization",
  computed(() => props.org?.id ?? ""),
);

async function goToSendMessage() {
  assertDefined(props.org);

  for (const conversation of conversations.value) {
    if (conversation.attributes.private === "false") continue;
    const participants = await conversation.getParticipants();
    if (participants.some((p) => p.identity === props.org!.id)) {
      return await navigateTo(href.value.messages.id(conversation.sid));
    }
  }

  targetUsersForConversationCreation.value = [props.org];
  await navigateTo(href.value.messages.id("_"));
}
</script>
